import type { PickEvent, Vec3, Vec4 } from "eutopia.ts";
import {
  billboard,
  container,
  createLayer,
  createLayerType,
  text,
} from "eutopia.ts";
import { $, map, type Properties, resolve } from "signlets";

import { positionEmpty } from "../position";
import { scaleOpacity } from "./color";
import { stem } from "./stem";

const markerPng = new URL("../world/marker.png", import.meta.url).toString();

export type MarkerProps = {
  markers: Properties<Marker>[];
};

export type Marker = {
  position: Vec3;
  color: Vec4;
  text?: string;
  pickable?: boolean;
  onMouseMove?: (event: PickEvent) => void;
  onClick?: (event: PickEvent) => void;
  onDrag?: (event: PickEvent) => void;
  onDragFlat?: (event: PickEvent) => void;
  onDragEnd?: (event: PickEvent) => void;
};

export const marker = createLayerType<MarkerProps>((context, { markers }) =>
  createLayer(
    context,
    container({
      layers: [
        stem({
          stems: markers,
        }),
        billboard({
          depth: false,
          billboards: map(
            markers,
            ({
              position,
              color,
              pickable,
              onMouseMove,
              onClick,
              onDrag,
              onDragFlat,
              onDragEnd,
            }) => ({
              image: markerPng,
              position,
              size: 1000,
              color: $(() =>
                scaleOpacity(
                  resolve(color),
                  !positionEmpty(resolve(position)) ? 1 : 0,
                ),
              ),
              outline: $(() =>
                scaleOpacity(
                  [0, 0, 0, 1],
                  !positionEmpty(resolve(position)) ? 1 : 0,
                ),
              ),
              minScale: 0.25,
              maxScale: 0.375,
              pickable,
              onMouseMove,
              onClick,
              onDrag,
              onDragFlat,
              onDragEnd,
            }),
          ),
        }),
        text({
          depth: false,
          entries: map(markers, ({ position, text, color }) => ({
            position,
            text: $(() => resolve(text) ?? ""),
            font: '"Exo 2", sans-serif',
            fontSize: 24,
            color: $(() => {
              const [, , , a] = resolve(color);
              return [0, 0, 0, a];
            }),
            size: 1,
            minScale: 1,
            maxScale: 1,
            pickable: false,
          })),
        }),
      ],
    }),
  ),
);
